import { useEffect, useState } from "react";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    DEFAULT_MESSAGE_SETTINGS,
    getMessageSettings,
    setMessageSetting,
    subscribeMessageSettings,
} from "@/lib/messageSettings";

const ROWS = [
    { key: "showOnlineStatus",     title: "Show online status",      caption: "Let others see when you're active." },
    { key: "showLastSeen",         title: "Show last seen",          caption: "Show the last time you were online." },
    { key: "sendReadReceipts",     title: "Send read receipts",      caption: "When off, you also won't see others'." },
    { key: "allowMessageRequests", title: "Allow message requests",  caption: "From people who don't follow you yet." },
    { key: "muteUnknownUsers",     title: "Mute unknown users",      caption: "Hide notifications from non-contacts." },
];

const MessageSettings = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [s, setS] = useState(() => (user ? getMessageSettings(user.id) : DEFAULT_MESSAGE_SETTINGS));

    useEffect(() => subscribeMessageSettings(() => user && setS(getMessageSettings(user.id))), [user]);

    const onChange = (key, val) => {
        if (!user) return;
        setMessageSetting(user.id, key, val);
        setS((prev) => ({ ...prev, [key]: val }));
    };

    return (
        <div className="screen" data-testid="message-settings-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between">
                <button onClick={() => navigate(-1)} data-testid="message-settings-back" className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted">
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">Messaging & privacy</h1>
                <span className="h-10 w-10" />
            </header>
            <div className="flex-1 overflow-y-auto no-scrollbar pb-10 px-4 pt-3">
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3 mb-4">
                    <span className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                        <MessageCircle size={18} />
                    </span>
                    <p className="text-[12.5px] text-muted-foreground">
                        Control what people see about you in chats. Changes apply instantly.
                    </p>
                </div>
                <ul className="space-y-2">
                    {ROWS.map((r) => (
                        <li
                            key={r.key}
                            data-testid={`message-settings-${r.key}`}
                            className="flex items-center gap-3 p-3.5 rounded-2xl border border-border bg-card"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-[13.5px] font-medium leading-tight">{r.title}</div>
                                <div className="text-[11.5px] text-muted-foreground mt-0.5">{r.caption}</div>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={!!s[r.key]}
                                data-testid={`message-settings-${r.key}-toggle`}
                                onClick={() => onChange(r.key, !s[r.key])}
                                className={[
                                    "relative h-7 w-12 rounded-full transition-colors shrink-0",
                                    s[r.key] ? "bg-primary" : "bg-muted",
                                ].join(" ")}
                            >
                                <span
                                    className={[
                                        "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-soft transition-[left]",
                                        s[r.key] ? "left-[22px]" : "left-0.5",
                                    ].join(" ")}
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default MessageSettings;
